import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

// Product Description & Basic Information
export const deSoiProductDescriptionSchema = yup
  .object({
    skuName: yup.string().nullable().optional(),
  })
  .required()

// Ingredient Information
export const deSoiIngredientSchema = yup
  .object({
    ingredientName: yup.string().nullable().optional(),
    quantity: yup.string().nullable().optional(),
    startingQuantityMultiplier: yup.string().nullable().optional(),
    uom: yup.string().nullable().optional(),
    manufacturerBrandName: yup.string().nullable().optional(),
    manufacturerItemNo: yup.string().nullable().optional(),
    notes: yup.string().nullable().optional(),
  })
  .required()

// Product Specifications
export const deSoiProductSpecificationsSchema = yup
  .object({
    ph: yup.string().nullable().optional(),
    phRange: yup.string().nullable().optional(),
    totalAcidity: yup.string().nullable().optional(),
    totalAcidityRange: yup.string().nullable().optional(),
    brix: yup.string().nullable().optional(),
    brixRange: yup.string().nullable().optional(),
    sulfitesFree: yup.string().nullable().optional(),
    sulfitesFreeRange: yup.string().nullable().optional(),
    sulfitesTotal: yup.string().nullable().optional(),
    sulfitesTotalRange: yup.string().nullable().optional(),
    abv: yup.string().nullable().optional(),
    abvRange: yup.string().nullable().optional(),
    carbonation: yup.string().nullable().optional(),
    carbonationRange: yup.string().nullable().optional(),
    density: yup.string().nullable().optional(),
    densityRange: yup.string().nullable().optional(),
  })
  .required()

// Process Steps
export const deSoiProcessStepSchema = yup
  .object({
    stepDescription: yup.string().nullable().optional(),
  })
  .required()

// Process Specifications
export const deSoiProcessSpecificationsSchema = yup
  .object({
    productStabilityMethod: yup.string().nullable().optional(),
    processSteps: yup.array().of(deSoiProcessStepSchema).required(),
  })
  .required()

// Coding
export const deSoiCodingSchema = yup
  .object({
    lotNum: yup.string().nullable().optional(),
    expirationDate: yup.string().nullable().optional(),
  })
  .required()

// Additional Notes
export const deSoiAdditionalNoteSchema = yup
  .object({
    note: yup.string().nullable().optional(),
  })
  .required()

// Main file data schema
export const deSoiProductInformationWorksheetCanXlsxFileDataSchema = yup
  .object({
    dateCreatedUpdated: yup.string().nullable().optional(),
    totalBatchVolumeGals: yup.string().nullable().optional(),
    productDescription: deSoiProductDescriptionSchema,
    ingredients: yup.array().of(deSoiIngredientSchema).required(),
    productSpecifications: deSoiProductSpecificationsSchema,
    processSpecifications: deSoiProcessSpecificationsSchema,
    coding: deSoiCodingSchema,
    customerSampleRetains: yup.string().nullable().optional(),
    additionalNotes: yup.array().of(deSoiAdditionalNoteSchema).optional(),
  })
  .required()

export type DeSoiProductInformationWorksheetCanXlsxFileData = yup.InferType<
  typeof deSoiProductInformationWorksheetCanXlsxFileDataSchema
>

export type DeSoiProductDescriptionData = yup.InferType<typeof deSoiProductDescriptionSchema>
export type DeSoiIngredientData = yup.InferType<typeof deSoiIngredientSchema>
export type DeSoiProductSpecificationsData = yup.InferType<typeof deSoiProductSpecificationsSchema>
export type DeSoiProcessStepData = yup.InferType<typeof deSoiProcessStepSchema>
export type DeSoiProcessSpecificationsData = yup.InferType<typeof deSoiProcessSpecificationsSchema>
export type DeSoiCodingData = yup.InferType<typeof deSoiCodingSchema>
export type DeSoiAdditionalNoteData = yup.InferType<typeof deSoiAdditionalNoteSchema>

// Placeholder mappings for now - these will be updated once we understand the specific field requirements
type RequiredFileData = MakeRequiredAndNullable<DeSoiProductInformationWorksheetCanXlsxFileData>

const productDescriptionMappings: MakeRequiredAndNullable<DeSoiProductDescriptionData> = {
  skuName: 'dab47fb5-9f60-4e64-b271-cab6ac6e03d3',
}

const ingredientMappings: MakeRequiredAndNullable<DeSoiIngredientData> = {
  ingredientName: '46004f50-cd50-4e44-b737-eb511bb77603',
  quantity: 'dc835a2f-5f05-4ea4-a2f4-af9cd7e6ef67',
  startingQuantityMultiplier: 'cd5ad0d8-1c9a-4a50-9064-e2d804b0fb10',
  uom: '9e4e59fb-8b73-41b9-96fe-8ffeb3b7c483',
  manufacturerBrandName: 'd079d422-1327-46b9-ad92-938173401481',
  manufacturerItemNo: '59af0ad5-5e73-49c1-b5e6-b639355b650e',
  notes: 'c99d7da4-7435-4b58-9369-ee2c43c19f57',
}

const productSpecificationsMappings: MakeRequiredAndNullable<DeSoiProductSpecificationsData> = {
  ph: '9078cd65-9070-4908-b8c4-f7c3ca299281',
  phRange: 'cf88708a-9caa-45da-b59c-9b029b1db22e',
  totalAcidity: '850c047b-23ec-4ebb-bbe8-761ae82b0539',
  totalAcidityRange: '17719fbe-5962-4f1e-a0fb-a207301102c1',
  brix: '716d2b6a-2fc1-444d-b13d-b732922a1ae9',
  brixRange: '4547991e-08ae-4b3f-bdb3-126a7f6d4635',
  sulfitesFree: '0df56b73-ebb2-40f8-b2dd-0c66a6e5cb0a',
  sulfitesFreeRange: '76e15e17-a0f1-4389-8da3-4faf538da1a4',
  sulfitesTotal: '4b67d67c-d2db-43b1-bd7d-162e3137ff82',
  sulfitesTotalRange: '24be48a5-95da-4595-988f-9865c5a45a7a',
  abv: '9ec828e4-d54d-4f92-9eec-7c2fbc578129',
  abvRange: '8d54b136-36e0-46d2-8fde-4beb98af6841',
  carbonation: '35757997-074f-4b91-8f03-867a430344f2',
  carbonationRange: 'a1aeaab3-1c44-4fae-8002-6ad4363c836a',
  density: 'f7f3f6bf-5706-4407-be73-58fd111a6b63',
  densityRange: '3acf60b5-3992-43b5-8428-61b7b9c69b75',
}

const processStepMappings: MakeRequiredAndNullable<DeSoiProcessStepData> = {
  stepDescription: '06646aca-870b-4e48-824b-b2b55578947b',
}

const processSpecificationsMappings: MakeRequiredAndNullable<DeSoiProcessSpecificationsData> = {
  productStabilityMethod: '410a59c6-89a4-4f0d-b909-4627228fb729',
  processSteps: [processStepMappings],
}

const codingMappings: MakeRequiredAndNullable<DeSoiCodingData> = {
  lotNum: '85cbb52b-c6d4-4740-8d9d-ae87b7366216',
  expirationDate: 'a3b7a87a-9347-4195-a43f-32466d94e4ab',
}

const additionalNotesMappings: MakeRequiredAndNullable<DeSoiAdditionalNoteData> = {
  note: 'f2ac5f7e-bf06-46bd-a27a-80c6eb06b783',
}

export const deSoiProductInformationWorksheetCanXlsxFileMappings: RequiredFileData = {
  dateCreatedUpdated: '6ed14eb5-7cdc-4153-96a3-837da4e744d3',
  totalBatchVolumeGals: 'c8b08bdf-c2e5-45ad-8bf0-48ac9a08d137',
  productDescription: productDescriptionMappings,
  ingredients: [ingredientMappings],
  productSpecifications: productSpecificationsMappings,
  processSpecifications: processSpecificationsMappings,
  coding: codingMappings,
  customerSampleRetains: '2307b611-04a9-4eb3-86f3-cbd99dfd585e',
  additionalNotes: [additionalNotesMappings],
}
